/**
 * FameSystem — Manages party fame/reputation score.
 * Fame ranges from -1000 (villain) to 1000 (legendary).
 * Affects vendor prices, town access, and NPC attitudes.
 *
 * @module FameSystem
 */

import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';
import WorldState from '../world/WorldState.js';

// ─── Constants ────────────────────────────────────────────────────────────────

const FAME_MIN = -1000;
const FAME_MAX = 1000;

/**
 * Fame tier definitions in ascending order.
 * @type {Array<{ tier: string, min: number, label: string, priceModifier: number }>}
 */
const FAME_TIERS = [
  { tier: 'villain',    min: -1000, label: 'Villain',    priceModifier: 1.5 },
  { tier: 'notorious',  min:  -499, label: 'Notorious',  priceModifier: 1.2 },
  { tier: 'unknown',    min:  -99,  label: 'Unknown',    priceModifier: 1.0 },
  { tier: 'known',      min:   101, label: 'Known',      priceModifier: 0.95 },
  { tier: 'respected',  min:   301, label: 'Respected',  priceModifier: 0.9 },
  { tier: 'legendary',  min:   701, label: 'Legendary',  priceModifier: 0.85 },
];

/**
 * Town types that refuse service to villains and notorious parties.
 * @type {Set<string>}
 */
const LAWFUL_TOWN_TYPES = new Set([
  'human_town',
  'dvergr_hold',
  'alfar_grove',
  'tomte_village',
  'trade_hub',
  'port_town',
]);

// ─── FameSystem ───────────────────────────────────────────────────────────────

const FameSystem = {
  /**
   * Get current fame value from WorldState.
   * @returns {number}
   */
  getFame() {
    return WorldState.partyFame ?? 0;
  },

  /**
   * Add fame (positive or negative). Clamps result to [-1000, 1000].
   * Emits 'fame:changed' with { old, new, delta }.
   * @param {number} delta
   * @returns {number} New fame value
   */
  addFame(delta) {
    if (typeof delta !== 'number' || !isFinite(delta)) {
      DebugLog.warn('[fame] addFame: invalid delta, ignoring');
      return this.getFame();
    }

    const oldFame = this.getFame();
    const newFame = Math.max(FAME_MIN, Math.min(FAME_MAX, oldFame + delta));
    WorldState.partyFame = newFame;

    const oldTier = this._tierFor(oldFame);
    const newTier = this._tierFor(newFame);

    DebugLog.event(
      `[fame] Fame changed; old=${oldFame} new=${newFame} delta=${delta} ` +
      `tier=${newTier}`
    );

    EventBus.emit('fame:changed', { old: oldFame, new: newFame, delta, tier: newTier });

    if (oldTier !== newTier) {
      DebugLog.info(`[fame] Tier changed: ${oldTier} → ${newTier}`);
      EventBus.emit('fame:tierChanged', { oldTier, newTier, fame: newFame });
    }

    return newFame;
  },

  /**
   * Get the fame tier key for the current fame value.
   * @returns {'villain'|'notorious'|'unknown'|'known'|'respected'|'legendary'}
   */
  getTier() {
    return this._tierFor(this.getFame());
  },

  /**
   * Get the display label for the current fame tier.
   * @returns {string}
   */
  getTierLabel() {
    const fame = this.getFame();
    const entry = this._getTierEntry(fame);
    return entry.label;
  },

  /**
   * Get the price modifier applied to vendor transactions for the current fame.
   * villain: 1.5x, notorious: 1.2x, unknown: 1.0x,
   * known: 0.95x, respected: 0.9x, legendary: 0.85x
   * @returns {number}
   */
  getPriceModifier() {
    return this._getTierEntry(this.getFame()).priceModifier;
  },

  /**
   * Check whether the party is hostile at a given town type.
   * Villain and notorious reputations are refused service at lawful towns.
   * Goblin fortresses refuse service to parties with fame >= 500.
   * @param {string} townTypeId
   * @returns {boolean}
   */
  isHostileAt(townTypeId) {
    const fame = this.getFame();
    const tier = this._tierFor(fame);

    if (townTypeId === 'goblin_fortress') {
      // Goblins welcome the infamous, distrust the renowned
      return fame >= 500;
    }

    if (LAWFUL_TOWN_TYPES.has(townTypeId)) {
      return tier === 'villain' || tier === 'notorious';
    }

    return false;
  },

  /**
   * Get a full fame summary object.
   * @returns {{ fame: number, tier: string, label: string, priceModifier: number }}
   */
  getSummary() {
    const fame = this.getFame();
    const entry = this._getTierEntry(fame);
    return {
      fame,
      tier: entry.tier,
      label: entry.label,
      priceModifier: entry.priceModifier,
    };
  },

  // ─── Internal helpers ──────────────────────────────────────────────────────

  /**
   * Get the tier key string for a given fame value.
   * @param {number} fame
   * @returns {string}
   */
  _tierFor(fame) {
    return this._getTierEntry(fame).tier;
  },

  /**
   * Get the full tier entry for a given fame value.
   * @param {number} fame
   * @returns {object}
   */
  _getTierEntry(fame) {
    // Walk tiers in descending order; first one whose min threshold is met wins
    for (let i = FAME_TIERS.length - 1; i >= 0; i--) {
      if (fame >= FAME_TIERS[i].min) {
        return FAME_TIERS[i];
      }
    }
    // Fallback: villain (fame <= -500 catch-all due to min=-1000 entry)
    return FAME_TIERS[0];
  },
};

export default FameSystem;
